/**
 * AvatarCanvas - Three.js 3D avatar with lip sync animation
 *
 * WHAT THIS DOES:
 * - Loads a 3D model (GLB file) of "Stella" the avatar
 * - Plays audio and synchronizes mouth movements using viseme data
 * - Provides orbit camera controls for user interaction
 *
 * LIP SYNC OVERVIEW:
 * The 15 Oculus visemes represent different mouth shapes:
 * - 0: sil (silence - mouth closed)
 * - 1-9: consonant sounds (PP, FF, TH, DD, kk, CH, SS, nn, RR)
 * - 10-14: vowel sounds (aa, E, I, O, U)
 *
 * These map to ARKit blend shapes on the 3D model (mouthOpen, mouthPucker, etc.)
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import type { LipSyncPoint } from '../services/tts'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AvatarCanvasProps {
  /** Called when the 3D model is loaded and ready */
  onReady?: () => void
  /** URL of audio to play (HTTP or data URL) */
  audioUrl?: string
  /** Viseme data for lip sync - each point has time (seconds) and value (0-14) */
  lipSync?: LipSyncPoint[]
  /** Called when audio playback ends */
  onAudioEnd?: () => void
  /** Called when audio playback starts */
  onAudioStart?: () => void
  /** CSS class for the container */
  className?: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Viseme to Blend Shape Mapping
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps Oculus viseme indices to ARKit blend shapes.
 *
 * Each viseme specifies which blend shapes to activate and at what weight.
 * Weights are intentionally subtle (0.1-0.35) for natural-looking animation.
 */
const VISEME_TO_BLENDSHAPE: Record<number, { shapes: string[]; weight: number }> = {
  0: { shapes: [], weight: 0 },                                    // sil - closed
  1: { shapes: ['mouthPucker', 'mouthClose'], weight: 0.25 },     // PP - p, b, m
  2: { shapes: ['mouthFunnel'], weight: 0.2 },                    // FF - f, v
  3: { shapes: ['tongueOut'], weight: 0.1 },                      // TH - th
  4: { shapes: ['mouthOpen'], weight: 0.15 },                     // DD - d, t, n
  5: { shapes: ['mouthOpen', 'jawOpen'], weight: 0.2 },           // kk - k, g
  6: { shapes: ['mouthFunnel'], weight: 0.2 },                    // CH - ch, j, sh
  7: { shapes: ['mouthSmile'], weight: 0.15 },                    // SS - s, z
  8: { shapes: ['mouthOpen'], weight: 0.1 },                      // nn - n, l
  9: { shapes: ['mouthPucker'], weight: 0.15 },                   // RR - r
  10: { shapes: ['mouthOpen', 'jawOpen'], weight: 0.35 },         // aa - wide open
  11: { shapes: ['mouthSmile', 'mouthOpen'], weight: 0.25 },      // E - e
  12: { shapes: ['mouthSmile'], weight: 0.15 },                   // I - i
  13: { shapes: ['mouthFunnel', 'jawOpen'], weight: 0.3 },        // O - o
  14: { shapes: ['mouthPucker', 'mouthFunnel'], weight: 0.25 },   // U - u
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function AvatarCanvas({
  onReady,
  audioUrl,
  lipSync,
  onAudioEnd,
  onAudioStart,
  className = '',
}: AvatarCanvasProps) {
  // DOM refs
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Three.js objects (stored in refs to persist across renders)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const controlsRef = useRef<OrbitControls | null>(null)
  const animationIdRef = useRef<number | null>(null)
  const clockRef = useRef<THREE.Clock | null>(null)
  const mixerRef = useRef<THREE.AnimationMixer | null>(null)

  // ─────────────────────────────────────────────────────────────────
  // Morph Target Management
  // ─────────────────────────────────────────────────────────────────

  /**
   * Stores morph target info for each mesh that has blend shapes.
   * Key: mesh name
   * Value: { mesh, dictionary (name→index), influences (current values) }
   */
  const morphTargetsRef = useRef<
    Map<
      string,
      {
        mesh: THREE.Mesh
        dictionary: Record<string, number>
        influences: number[]
      }
    >
  >(new Map())

  /**
   * Fast lookup cache: blend shape name → array of {mesh, index}.
   * Built once on model load to avoid iteration during animation.
   */
  const morphTargetCacheRef = useRef<
    Map<string, Array<{ mesh: THREE.Mesh; index: number }>>
  >(new Map())

  /**
   * Bone cache for arm positioning (applied once on load).
   */
  const boneCacheRef = useRef<{
    leftUpperArm: THREE.Bone[]
    rightUpperArm: THREE.Bone[]
    leftForearm: THREE.Bone[]
    rightForearm: THREE.Bone[]
  }>({
    leftUpperArm: [],
    rightUpperArm: [],
    leftForearm: [],
    rightForearm: [],
  })

  // Audio refs
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isPlayingRef = useRef(false)
  const currentVisemeIndexRef = useRef(0)

  // Loading state
  const [isLoading, setIsLoading] = useState(true)
  const [loadingProgress, setLoadingProgress] = useState(0)

  // Stable callback refs (to avoid stale closures)
  const onAudioStartRef = useRef(onAudioStart)
  const onAudioEndRef = useRef(onAudioEnd)
  const onReadyRef = useRef(onReady)
  const lipSyncRef = useRef(lipSync)

  useEffect(() => {
    onAudioStartRef.current = onAudioStart
    onAudioEndRef.current = onAudioEnd
    onReadyRef.current = onReady
    lipSyncRef.current = lipSync
  }, [onAudioStart, onAudioEnd, onReady, lipSync])

  // ─────────────────────────────────────────────────────────────────
  // Morph Target Functions
  // ─────────────────────────────────────────────────────────────────

  /** Reset all morph targets to zero (neutral face) */
  const resetMorphTargets = useCallback(() => {
    morphTargetsRef.current.forEach(({ mesh, influences }) => {
      for (let i = 0; i < influences.length; i++) {
        influences[i] = 0
      }
      mesh.morphTargetInfluences = influences
    })
  }, [])

  /** Build the fast-lookup cache for blend shapes */
  const buildMorphTargetCache = useCallback(() => {
    const cache = new Map<string, Array<{ mesh: THREE.Mesh; index: number }>>()

    // All blend shape names we might need
    const shapeNames = [
      'mouthSmileLeft',
      'mouthSmileRight',
      'mouthSmile',
      'smile',
      'mouthPucker',
      'mouthClose',
      'mouthFunnel',
      'tongueOut',
      'mouthOpen',
      'jawOpen',
    ]

    morphTargetsRef.current.forEach(({ mesh, dictionary }) => {
      shapeNames.forEach((name) => {
        // Try common naming variations
        const variations = [name, name.toLowerCase(), name.charAt(0).toUpperCase() + name.slice(1)]
        for (const variant of variations) {
          if (dictionary[variant] !== undefined) {
            if (!cache.has(name)) {
              cache.set(name, [])
            }
            cache.get(name)!.push({ mesh, index: dictionary[variant] })
            break
          }
        }
      })
    })

    morphTargetCacheRef.current = cache
  }, [])

  /** Apply a subtle smile (idle expression) */
  const applySmile = useCallback((intensity: number = 0.2) => {
    const cache = morphTargetCacheRef.current
    const smileShapes = ['mouthSmileLeft', 'mouthSmileRight', 'mouthSmile', 'smile']

    smileShapes.forEach((name) => {
      const targets = cache.get(name)
      targets?.forEach(({ mesh, index }) => {
        const influences = mesh.morphTargetInfluences
        if (influences) {
          influences[index] = intensity
        }
      })
    })
  }, [])

  /** Apply a viseme to the face (used during speech) */
  const applyViseme = useCallback(
    (visemeIndex: number, weight: number = 1) => {
      const mapping = VISEME_TO_BLENDSHAPE[visemeIndex]
      if (!mapping) return

      // Reset to neutral first
      resetMorphTargets()

      const cache = morphTargetCacheRef.current
      const targetWeight = mapping.weight * weight

      // Apply the blend shapes for this viseme
      mapping.shapes.forEach((shapeName) => {
        const targets = cache.get(shapeName)
        targets?.forEach(({ mesh, index }) => {
          const influences = mesh.morphTargetInfluences
          if (influences) {
            influences[index] = targetWeight
          }
        })
      })
    },
    [resetMorphTargets]
  )

  // ─────────────────────────────────────────────────────────────────
  // Scene Setup (runs once on mount)
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    /** Initialize Three.js scene when container has dimensions */
    const initScene = () => {
      const width = container.clientWidth
      const height = container.clientHeight

      // Wait for container to have size
      if (width === 0 || height === 0) {
        requestAnimationFrame(initScene)
        return
      }

      // Create scene with dark background
      const scene = new THREE.Scene()
      scene.background = new THREE.Color(0x1a1a2e)
      sceneRef.current = scene

      // Create camera (narrow FOV for portrait-like view)
      const camera = new THREE.PerspectiveCamera(25, width / height, 0.1, 1000)
      camera.position.set(0, 1.6, 2) // Positioned to frame head/shoulders
      cameraRef.current = camera

      // Create renderer with quality settings
      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
      })
      renderer.setSize(width, height)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.shadowMap.enabled = false // Avoid blocky artifacts
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.outputColorSpace = THREE.SRGBColorSpace
      rendererRef.current = renderer

      // Add orbit controls (user can rotate camera)
      const controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.05
      controls.target.set(0, 1.5, 0) // Look at neck height
      controls.minDistance = 1
      controls.maxDistance = 4
      controls.maxPolarAngle = Math.PI / 2 + 0.2 // Limit vertical rotation
      controlsRef.current = controls

      // Add lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.4)
      scene.add(ambientLight)

      const mainLight = new THREE.DirectionalLight(0xffffff, 0.8)
      mainLight.position.set(5, 5, 5)
      scene.add(mainLight)

      const fillLight = new THREE.DirectionalLight(0x88ccff, 0.3)
      fillLight.position.set(-5, 3, -5)
      scene.add(fillLight)

      // Clock for animation timing
      const clock = new THREE.Clock()
      clockRef.current = clock

      // Load the 3D model
      const loader = new GLTFLoader()

      loader.load(
        '/models/stella.glb',
        (gltf) => {
          const model = gltf.scene
          model.position.set(0, 0, 0)
          model.scale.set(1, 1, 1)

          // Process all meshes and bones in the model
          model.traverse((child) => {
            // Collect morph targets from meshes (for lip sync)
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh
              mesh.frustumCulled = false

              if (mesh.morphTargetDictionary && mesh.morphTargetInfluences) {
                morphTargetsRef.current.set(mesh.name, {
                  mesh,
                  dictionary: mesh.morphTargetDictionary,
                  influences: [...mesh.morphTargetInfluences],
                })
              }
            }

            // Position arms in a natural pose
            const bone = child as THREE.Bone
            if (bone.isBone) {
              const boneName = bone.name.toLowerCase()

              // Left arm positioning
              if (
                boneName.includes('leftupperarm') ||
                boneName.includes('left_upperarm') ||
                boneName.includes('leftarm') ||
                boneName.includes('left_arm') ||
                boneName.includes('shoulder_l') ||
                boneName.includes('leftshoulder')
              ) {
                boneCacheRef.current.leftUpperArm.push(bone)
                bone.rotation.z = (-45 * Math.PI) / 180
                bone.rotation.y = (-25.5 * Math.PI) / 180
              }

              // Right arm positioning
              if (
                boneName.includes('rightupperarm') ||
                boneName.includes('right_upperarm') ||
                boneName.includes('rightarm') ||
                boneName.includes('right_arm') ||
                boneName.includes('shoulder_r') ||
                boneName.includes('rightshoulder')
              ) {
                boneCacheRef.current.rightUpperArm.push(bone)
                bone.rotation.z = (49 * Math.PI) / 180
                bone.rotation.y = (24.5 * Math.PI) / 180
              }

              // Forearm positioning
              if (
                boneName.includes('leftforearm') ||
                boneName.includes('left_forearm') ||
                boneName.includes('leftlowerarm') ||
                boneName.includes('left_lowerarm')
              ) {
                boneCacheRef.current.leftForearm.push(bone)
                bone.rotation.x = (3 * Math.PI) / 180
              }

              if (
                boneName.includes('rightforearm') ||
                boneName.includes('right_forearm') ||
                boneName.includes('rightlowerarm') ||
                boneName.includes('right_lowerarm')
              ) {
                boneCacheRef.current.rightForearm.push(bone)
                bone.rotation.x = (3 * Math.PI) / 180
              }
            }
          })

          scene.add(model)

          // Setup animation mixer if model has animations
          if (gltf.animations?.length > 0) {
            mixerRef.current = new THREE.AnimationMixer(model)
          }

          // Build caches and apply initial expression
          buildMorphTargetCache()
          applySmile(0.15)

          setIsLoading(false)
          onReadyRef.current?.()
        },
        (progress) => {
          if (progress.total > 0) {
            setLoadingProgress((progress.loaded / progress.total) * 100)
          }
        },
        () => {
          // Error loading - still notify ready so app can continue
          setIsLoading(false)
          onReadyRef.current?.()
        }
      )

      // Animation loop
      const animate = () => {
        animationIdRef.current = requestAnimationFrame(animate)

        const delta = clock.getDelta()
        controls.update()

        if (mixerRef.current) {
          mixerRef.current.update(delta)
        }

        renderer.render(scene, camera)
      }

      animationIdRef.current = requestAnimationFrame(animate)
    }

    initScene()

    // Cleanup on unmount
    return () => {
      if (animationIdRef.current !== null) {
        cancelAnimationFrame(animationIdRef.current)
      }
      rendererRef.current?.dispose()
      controlsRef.current?.dispose()

      morphTargetsRef.current.clear()
      morphTargetCacheRef.current.clear()
      boneCacheRef.current = {
        leftUpperArm: [],
        rightUpperArm: [],
        leftForearm: [],
        rightForearm: [],
      }
      sceneRef.current = null
      cameraRef.current = null
      rendererRef.current = null
      controlsRef.current = null
      mixerRef.current = null
    }
  }, [buildMorphTargetCache, applySmile])

  // ─────────────────────────────────────────────────────────────────
  // Window Resize Handler
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleResize = () => {
      const width = container.clientWidth
      const height = container.clientHeight

      if (width === 0 || height === 0) return

      if (cameraRef.current) {
        cameraRef.current.aspect = width / height
        cameraRef.current.updateProjectionMatrix()
      }

      rendererRef.current?.setSize(width, height)
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)
    window.addEventListener('resize', handleResize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // ─────────────────────────────────────────────────────────────────
  // Audio Playback & Lip Sync Animation
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!audioUrl) {
      resetMorphTargets()
      isPlayingRef.current = false
      return
    }

    // Create or reuse audio element
    let audio = audioRef.current
    if (!audio) {
      audio = new Audio()
      audioRef.current = audio
    }

    currentVisemeIndexRef.current = 0
    isPlayingRef.current = false

    let animationFrameId: number | null = null

    /**
     * Animation loop for lip sync.
     * Finds the current viseme based on audio time and applies it.
     */
    const animateMouth = () => {
      if (!isPlayingRef.current || !audio) {
        resetMorphTargets()
        return
      }

      const currentTime = audio.currentTime
      const lipSyncData = lipSyncRef.current || []

      // Find current viseme (advance until we find one past current time)
      let visemeIndex = currentVisemeIndexRef.current
      while (visemeIndex < lipSyncData.length - 1 && lipSyncData[visemeIndex + 1].time <= currentTime) {
        visemeIndex++
      }
      currentVisemeIndexRef.current = visemeIndex

      const currentViseme = lipSyncData[visemeIndex]
      const nextViseme = lipSyncData[visemeIndex + 1]

      if (currentViseme) {
        let weight = 1

        // Interpolate towards next viseme for smoother animation
        if (nextViseme) {
          const segmentDuration = nextViseme.time - currentViseme.time
          const elapsed = currentTime - currentViseme.time
          const t = Math.max(0, Math.min(elapsed / segmentDuration, 1))
          weight = 1 - t * 0.3 // Slight ease-out
        }

        applyViseme(currentViseme.value, weight)
      } else {
        resetMorphTargets()
      }

      animationFrameId = requestAnimationFrame(animateMouth)
    }

    // Audio event handlers
    const handleCanPlay = () => {
      audio!
        .play()
        .then(() => {
          isPlayingRef.current = true
          onAudioStartRef.current?.()
          animationFrameId = requestAnimationFrame(animateMouth)
        })
        .catch(() => {
          onAudioEndRef.current?.()
        })
    }

    const handleEnded = () => {
      isPlayingRef.current = false
      currentVisemeIndexRef.current = 0
      resetMorphTargets()
      applySmile(0.2) // Return to subtle smile

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }

      onAudioEndRef.current?.()
    }

    const handleError = () => {
      isPlayingRef.current = false
      resetMorphTargets()

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }

      onAudioEndRef.current?.()
    }

    // Set up audio and start loading
    audio.src = audioUrl
    audio.addEventListener('canplay', handleCanPlay, { once: true })
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.load()

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      audio!.removeEventListener('canplay', handleCanPlay)
      audio!.removeEventListener('ended', handleEnded)
      audio!.removeEventListener('error', handleError)
      audio!.pause()
      audio!.src = ''
    }
  }, [audioUrl, applyViseme, applySmile, resetMorphTargets])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
        audioRef.current = null
      }
    }
  }, [])

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-assess-card flex items-center justify-center z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading Stella... {loadingProgress.toFixed(0)}%</p>
          </div>
        </div>
      )}

      {/* Three.js Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ touchAction: 'none' }} />
    </div>
  )
}
